import {
    fileBytesToFortuneSheets,
    buildXlsxBuffer as buildXlsxBufferOnMainThread,
    downloadAsXlsx as downloadAsXlsxOnMainThread,
    downloadXlsxBuffer,
    toCelldataSheet,
} from './converters';
import type { FortuneSheet } from './types';
import type { SheetWorkerRequest, SheetWorkerResponse } from './sheetWorkerProtocol';

/**
 * Worker-backed front door for the heavy spreadsheet conversions. Parsing a
 * large xlsx (unzip + XML DOM + per-cell conversion) blocks the main thread
 * for seconds — long enough for the browser's "Page unresponsive" prompt.
 * These wrappers run the same converters code inside a dedicated worker and
 * fall back to the main-thread implementations when workers are unavailable
 * (jsdom tests) or the worker itself fails to boot.
 *
 * The worker itself is a lazily-created SINGLETON, reused across every job
 * (parse, build, combined build) instead of a fresh create+terminate per
 * call — re-opening a history version no longer pays worker boot cost on top
 * of the parse. Jobs are queued and posted one at a time (simplest correct:
 * a large parse and a large serialize sharing one worker heap concurrently is
 * a real memory-pressure risk this app hasn't needed to take on); each
 * request/response already carries a monotonic `id` (sheetWorkerProtocol.ts)
 * used to route a response to its own caller regardless of queueing.
 */

let nextJobId = 1;
let sharedWorker: Worker | null = null;
const pendingJobs = new Map<number, { resolve: (r: SheetWorkerResponse) => void; reject: (err: unknown) => void }>();
const jobQueue: SheetWorkerRequest[] = [];
let jobInFlight = false;

const postNextJob = () => {
    if (jobInFlight || !sharedWorker) return;
    const request = jobQueue.shift();
    if (!request) return;
    jobInFlight = true;
    sharedWorker.postMessage(request);
};

// The worker script itself failed to load/boot (CSP, chunk 404 after a
// deploy, an uncaught exception escaping the message handler, …) — fail
// every pending/queued job so each caller falls back to the main-thread path,
// then drop the worker so the NEXT call creates a fresh one (self-healing).
const failAllJobs = (err: Error) => {
    pendingJobs.forEach((job) => job.reject(err));
    pendingJobs.clear();
    jobQueue.length = 0;
    jobInFlight = false;
    sharedWorker = null;
};

const getSharedWorker = async (): Promise<Worker> => {
    if (sharedWorker) return sharedWorker;
    // Dynamic import so jest never parses createSheetWorker's `import.meta`
    // (the `typeof Worker` guards in the callers keep this path off in jsdom).
    const { createSheetWorker } = await import('./createSheetWorker');
    const worker = createSheetWorker();
    worker.onmessage = (e: MessageEvent<SheetWorkerResponse>) => {
        const job = pendingJobs.get(e.data?.id);
        if (!job) return;
        pendingJobs.delete(e.data.id);
        jobInFlight = false;
        job.resolve(e.data);
        postNextJob();
    };
    worker.onerror = (e) => failAllJobs(new Error(e.message || 'sheet worker failed'));
    sharedWorker = worker;
    return worker;
};

const runWorkerJob = async (buildRequest: (id: number) => SheetWorkerRequest): Promise<SheetWorkerResponse> => {
    await getSharedWorker();
    const id = nextJobId++;
    return new Promise<SheetWorkerResponse>((resolve, reject) => {
        pendingJobs.set(id, { resolve, reject });
        jobQueue.push(buildRequest(id));
        postNextJob();
    });
};

/**
 * Parse spreadsheet bytes into Fortune-Sheet sheets off the main thread.
 * A structured `{ok:false}` response means the FILE is bad (corrupt/unsupported)
 * — rethrown as-is, since retrying on the main thread would fail identically.
 * Only worker-boot failures fall back to the main-thread parser.
 */
export const parseSheetBytes = async (buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<FortuneSheet[]> => {
    if (typeof Worker === 'undefined') return fileBytesToFortuneSheets(buffer, mimeType, fileName);
    let response: SheetWorkerResponse;
    try {
        // Not passed as a transferable: keeping the buffer intact lets the
        // main-thread fallback below reuse it if the worker fails to boot.
        response = await runWorkerJob((id) => ({ id, kind: 'parse', buffer, mimeType, fileName }));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, parsing on main thread', err);
        return fileBytesToFortuneSheets(buffer, mimeType, fileName);
    }
    if (!response.ok) throw new Error(response.error);
    return (response as Extract<SheetWorkerResponse, { kind: 'parse' }>).sheets;
};

/**
 * Read a File object into FortuneSheet[] (import flow: xlsx/xls/csv). Same
 * contract as converters.importFileToSheets but parsed off the main thread.
 */
export const importFileToSheets = async (file: File): Promise<FortuneSheet[]> => {
    const buffer = await file.arrayBuffer();
    return parseSheetBytes(buffer, file.type, file.name);
};

/**
 * Serialize sheets to .xlsx bytes off the main thread. Returns the raw
 * ArrayBuffer — suitable for upload or any non-download use. Falls back to the
 * main-thread converters.buildXlsxBuffer when workers are unavailable.
 */
export const buildXlsxBytes = async (sheets: FortuneSheet[]): Promise<ArrayBuffer> => {
    if (typeof Worker === 'undefined') return buildXlsxBufferOnMainThread(sheets);
    let response: SheetWorkerResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx', sheets }));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, serializing on main thread', err);
        return buildXlsxBufferOnMainThread(sheets);
    }
    if (!response.ok) throw new Error(response.error);
    return (response as Extract<SheetWorkerResponse, { kind: 'build-xlsx' }>).buffer;
};

/**
 * Serialize RUNTIME sheets (2D `data`, as read from `getAllSheets()`) to .xlsx
 * bytes in ONE worker round trip — the worker runs `toCelldataSheet` (the
 * per-cell walk) and `buildXlsxBuffer` together, instead of the main thread
 * walking celldata first and then cloning that result across the boundary.
 * Falls back to the main-thread converters (celldata walk + buffer build) when
 * workers are unavailable.
 */
export const buildXlsxFromRuntimeSheets = async (sheets: FortuneSheet[]): Promise<ArrayBuffer> => {
    if (typeof Worker === 'undefined') return buildXlsxBufferOnMainThread(sheets.map(toCelldataSheet));
    let response: SheetWorkerResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx-from-runtime', sheets }));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, serializing on main thread', err);
        return buildXlsxBufferOnMainThread(sheets.map(toCelldataSheet));
    }
    if (!response.ok) throw new Error(response.error);
    return (response as Extract<SheetWorkerResponse, { kind: 'build-xlsx-from-runtime' }>).buffer;
};

/**
 * Export sheets to .xlsx and download — serialization runs in the worker, only
 * the Blob + anchor-click happen on the main thread. Falls back to the fully
 * main-thread converters.downloadAsXlsx (also keeps existing test mocks valid).
 */
export const downloadAsXlsx = async (sheets: FortuneSheet[], fileName: string): Promise<void> => {
    if (typeof Worker === 'undefined') return downloadAsXlsxOnMainThread(sheets, fileName);
    let response: SheetWorkerResponse;
    try {
        response = await runWorkerJob((id) => ({ id, kind: 'build-xlsx', sheets }));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[SheetEditor] sheet worker unavailable, exporting on main thread', err);
        return downloadAsXlsxOnMainThread(sheets, fileName);
    }
    if (!response.ok) throw new Error(response.error);
    downloadXlsxBuffer((response as Extract<SheetWorkerResponse, { kind: 'build-xlsx' }>).buffer, fileName);
};
